import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import InstrumentRow from '../common/InstrumentRow';
import ModalAction from '../common/ModalAction';
import RhythmStylePicker from '../common/RhythmStylePicker';
import SoundWave from '../common/SoundWave';
import { ChordBarsEffects, ChordBarsMixer } from '../ChordBarsTransport';
import useChordBarsPreview from '../../hooks/useChordBarsPreview';
import useSpacePlay from '../../hooks/useSpacePlay';
import { canChart } from '../../utils/parts';
import { isNotePart } from '../../utils/notes';
import { isSectionPart } from '../../utils/partNumbers';
import {
  carryPadModes,
  getIntensityLabel,
  getPadGain,
  getPassIntensity,
  INTENSITY_LEVELS,
  LAYER_OFF,
  LAYER_ON,
  NORMAL,
  PAD_CUT,
  PAD_KEEP,
  PLAYER_IDS,
  toIntensityEntry,
} from '../../utils/chartIntensity';
import { getStylePlayers } from '../../utils/chordBand';
import { getSongRunEntries } from '../../utils/songRun';
import { getBeatsPerBar } from '../../utils/songTiming';
import { hasEffects } from '../../utils/effectSends';
import { getHistoryShortcut } from '../../utils/editHistory';
import { settingKeys } from '../../utils/settings';
import { BLOCK_CHORDS } from '../../utils/chordRhythmPatterns';
import { DEFAULT_INSTRUMENT_ID, getChartInstrumentOverride } from '../../utils/midiInstruments';

import { ReactComponent as AcousticIcon } from '../../icons/acoustic-guitar.svg';
import { ReactComponent as BassIcon } from '../../icons/bass-guitar.svg';
import { ReactComponent as BrassIcon } from '../../icons/trumpet.svg';
import { ReactComponent as DrumIcon } from '../../icons/drum.svg';
import { ReactComponent as FillBarIcon } from '../../icons/fill-bar.svg';
import { ReactComponent as GuitarIcon } from '../../icons/electric-guitar.svg';
import { ReactComponent as IntensityIcon } from '../../icons/intensity.svg';
import { ReactComponent as LightBulbIcon } from '../../icons/light-bulb.svg';
import { ReactComponent as NotesIcon } from '../../icons/music-double-note.svg';
import { ReactComponent as PadIcon } from '../../icons/pad-wave.svg';
import { ReactComponent as PlayIcon } from '../../icons/play-solid.svg';
import { ReactComponent as RedoIcon } from '../../icons/redo.svg';
import { ReactComponent as RepeatIcon } from '../../icons/repeat.svg';
import { ReactComponent as RestIcon } from '../../icons/rest-quarter.svg';
import { ReactComponent as ReverbIcon } from '../../icons/reverb.svg';
import { ReactComponent as SettingsIcon } from '../../icons/settings.svg';
import { ReactComponent as SoundIcon } from '../../icons/music-single-note.svg';
import { ReactComponent as SlidersIcon } from '../../icons/sliders.svg';
import { ReactComponent as StopIcon } from '../../icons/square-rounded.svg';
import { ReactComponent as UndoIcon } from '../../icons/undo.svg';
import { ReactComponent as ViolinIcon } from '../../icons/violin.svg';

import './IntensityPanel.scss';

/** Whether a part plays through, and so has an intensity to set. */
export const canArrange = (part) => !isNotePart(part) && !isSectionPart(part) && canChart(part.name);

const repeatsOf = (part) => Math.max(1, Math.round(part.repeatCount) || 1);

// The level a part plays through at, as its stored fields give it
const levelOf = (part) => part.chartIntensity || NORMAL;

const passesOf = (part) => ({ ...toIntensityEntry(part), repeatCount: part.repeatCount });

// No pad setting of its own: the part carries on with whatever an earlier one set
const PAD_AS_BEFORE = 'before';

// Worded under `intensity.pad.<id>`, with a note at `<id>Note`
const PAD_OPTIONS = [PAD_AS_BEFORE, PAD_KEEP, PAD_CUT];

// A player left to the level, which plays it where the level brings it in
const LAYER_AUTO = 'auto';

// Worded under `intensity.<id>`
const LAYER_OPTIONS = [LAYER_AUTO, LAYER_ON, LAYER_OFF];

const PLAYERS = {
  bass: { labelKey: 'player.bass', Icon: BassIcon },
  brass: { labelKey: 'player.band.brass', Icon: BrassIcon },
  guitar: { labelKey: 'player.band.guitar', Icon: GuitarIcon },
  acoustic: { labelKey: 'player.band.acoustic', Icon: AcousticIcon },
  violin: { labelKey: 'player.band.violin', Icon: ViolinIcon },
  drums: { labelKey: 'player.drums', Icon: DrumIcon },
};

/**
 * How hard each part plays through: the song's shape as a row of bars, tapped to pick parts, and
 * the level, lead-in and pad to give every part picked. One part picked with repeats can also be set
 * pass by pass, and the parts picked can be given an instrument and rhythm of their own. Suggest
 * gives the whole song the usual arc at once, and Play plays it through as it stands, from the
 * first part picked, lighting each part as it plays. Undo and Redo step through
 * what it changed, from the toolbar or Cmd+Z / Cmd+Shift+Z / Cmd+Y.
 *
 * @param {object} song - `{ bpm, timeSignature, chartInstrumentId, chartRhythm, category }`, to play it with
 */
const IntensityPanel = ({
  parts,
  partNumbers,
  selectedIds,
  song,
  onToggle,
  onSelect,
  onApply,
  onReset,
  onSuggest,
  onDone,
  onEffectsChange = null,
  onRhythmChange = null,
  onInstrumentChange = null,
  onUndo = null,
  onRedo = null,
  canUndo = false,
  canRedo = false,
}) => {
  const { t } = useTranslation();
  const [isMixerOpen, setIsMixerOpen] = useState(false);
  // Where Play starts, settled as it's pressed so picking parts while it plays doesn't restart it
  const [playFrom, setPlayFrom] = useState(null);
  const [wantsPlay, setWantsPlay] = useState(false);

  const arrangeable = parts.filter(canArrange);
  const selected = arrangeable.filter((part) => selectedIds.includes(part.id));
  // With nothing picked, Reset takes the whole song back to Normal
  const resetScope = selected.length > 0 ? selected : arrangeable;
  const shared = (read) =>
    selected.length > 0 && selected.every((part) => read(part) === read(selected[0])) ? read(selected[0]) : null;
  const level = shared(levelOf);
  const fill = shared((part) => part.chartFill !== false);
  const allPicked = selected.length === arrangeable.length;
  const drop = shared((part) => Boolean(part.chartDrop));
  const padSetting = shared((part) => part.chartPad || PAD_AS_BEFORE);
  // Whether any part Reset would touch has something set for it to clear
  const isSet = resetScope.some(
    (part) =>
      part.chartIntensity ||
      part.chartIntensityPasses ||
      part.chartBuild ||
      part.chartFill === false ||
      part.chartDrop ||
      part.chartPad ||
      part.chartLayers,
  );
  const single = selected.length === 1 && repeatsOf(selected[0]) > 1 ? selected[0] : null;
  // The parts the pad is kept on under, on any pass, the setting carried from part to part
  const padHeldIds = useMemo(() => {
    const modes = carryPadModes(
      parts.map((part) => (isSectionPart(part) ? { section: true } : { ...passesOf(part), partId: part.id })),
    );

    return new Set(
      modes
        .filter(
          (entry) =>
            entry.padMode === PAD_KEEP &&
            Array.from({ length: Math.max(1, Math.round(entry.repeatCount) || 1) }, (unused, i) =>
              getPadGain(entry, getPassIntensity(entry, i + 1)),
            ).some(Boolean),
        )
        .map((entry) => entry.partId),
    );
  }, [parts]);

  const { bpm, timeSignature, chartInstrumentId, chartRhythm, category, key, sourceKey } = song;
  const songInstrumentId = chartInstrumentId || DEFAULT_INSTRUMENT_ID;
  const songRhythm = chartRhythm || BLOCK_CHORDS;
  // A part's own sound, else the song's; picking the song's again puts it back to following it
  const instrumentOf = (part) => getChartInstrumentOverride(part) || songInstrumentId;
  const rhythmOf = (part) => part.chartRhythm || songRhythm;
  const partInstrument = shared(instrumentOf);
  const partRhythm = shared(rhythmOf);
  const ownSoundCount = arrangeable.filter(
    (part) => instrumentOf(part) !== songInstrumentId || rhythmOf(part) !== songRhythm,
  ).length;
  const allEntries = useMemo(
    () => getSongRunEntries(parts, { bpm, timeSignature, chartInstrumentId, chartRhythm, key, sourceKey }, canArrange),
    [parts, bpm, timeSignature, chartInstrumentId, chartRhythm, key, sourceKey],
  );
  const entries = useMemo(() => {
    const from = allEntries.findIndex((entry) => entry.partId === playFrom);

    return from > 0 ? allEntries.slice(from) : allEntries;
  }, [allEntries, playFrom]);

  const player = useChordBarsPreview(null, {
    bpm,
    timeSignature,
    category,
    isActive: true,
    repeats: true,
    entries: entries.length > 0 ? entries : null,
    effects: song.chartEffects,
    onEffectsChange,
    melodySetting: settingKeys.intensityMelody,
  });
  const { play } = player;

  // Played once the run from the new start is built, which a changed start tears down and rebuilds
  useEffect(() => {
    if (!wantsPlay || !player.canPlay) return;

    setWantsPlay(false);
    play();
  }, [wantsPlay, player.canPlay, play]);

  // The page behind stays put, and Escape closes the panel. Through a ref, so a new onDone each
  // render doesn't put the listener back every time.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const previous = document.body.style.overflow;
    const onKey = (event) => event.key === 'Escape' && onDoneRef.current();

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // The players any part picked has in its style, to switch on or off; a drum solo has only its drums
  const players = PLAYER_IDS.filter((id) =>
    selected.some((part) => {
      const entry = allEntries.find((candidate) => candidate.partId === part.id);

      return (
        entry &&
        !entry.drumsOnly &&
        getStylePlayers(entry.rhythm, { beatsPerBar: getBeatsPerBar(timeSignature), category }).includes(id)
      );
    }),
  );

  // The first part picked that has a chart to play
  const startPart = selected.find((part) => allEntries.some((entry) => entry.partId === part.id));

  // A field being typed into keeps its own text undo
  useEffect(() => {
    if (!onUndo || !onRedo) return undefined;

    const handleKeyDown = (event) => {
      const { tagName, isContentEditable } = event.target;
      const isTyping = isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
      const step = getHistoryShortcut(event);

      if (!step || isTyping) return;

      event.preventDefault();

      if (step === 'redo') onRedo();
      else onUndo();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onUndo, onRedo]);

  const startPlaying = () => {
    setPlayFrom(startPart?.id ?? null);
    setWantsPlay(true);
  };

  // Space plays from the part picked, as Play does, and stops
  useSpacePlay(
    {
      isPlaying: player.isPlaying,
      canPlay: allEntries.length > 0 && player.canPlay,
      play: startPlaying,
      stop: player.stop,
    },
    true,
  );

  const segment = player.isPlaying ? player.segments[player.currentSegment] : null;
  const playingId = segment ? entries[segment.entryIndex]?.partId : null;
  const playingPart = arrangeable.find((part) => part.id === playingId);
  const partName = (part) => t(`parts.${part.name}`, { defaultValue: part.name });
  const fullName = (part) => `${partName(part)} ${partNumbers[part.id] ?? ''}`.trim();
  // The first letter, unless that would read as another part's (Pre-coro and Puente)
  const shortName = (part) =>
    `${t(`intensity.short.${part.name}`, { defaultValue: partName(part).charAt(0) })}${partNumbers[part.id] ?? ''}`;
  const hasDrops = arrangeable.some((part) => part.chartDrop);

  let status = startPart ? t('intensity.from', { part: fullName(startPart) }) : t('intensity.fromTop');
  if (playingPart) {
    status = t('intensity.playing', { part: fullName(playingPart), level: getIntensityLabel(segment.intensity) });
  } else if (allEntries.length === 0) status = t('intensity.noCharts');

  // The sheets' own Play starts from the part picked, as the toolbar's does
  const partPlayer = { ...player, play: startPlaying };
  const instrumentBrowser = (id) => ({
    downloadedIds: player.samples.downloadedIds,
    playingId: player.isPlaying ? id : undefined,
    loadingId: player.isLoading || player.samples.status === 'loading' ? id : undefined,
    progress: { loaded: player.samples.loaded, total: player.samples.total },
  });

  const setPass = (index, value) => {
    const passes = Array.from({ length: repeatsOf(single) }, (unused, i) => single.chartIntensityPasses?.[i] ?? null);

    passes[index] = value;
    onApply({ passes });
  };

  const panel = (
    <div className="IntensityPanel">
      <div className="toolbar">
        {/* Through the count-in too, which is part of playing */}
        {player.isPlaying && <SoundWave getSpectrum={player.getSpectrum} countInBeat={player.countInBeat} />}
        <span className="status">{player.error || status}</span>
        <ModalAction
          icon={player.isPlaying ? <StopIcon className="solid small" /> : <PlayIcon className="solid" />}
          label={player.playLabel}
          active={player.isPlaying}
          busy={player.playBusy}
          progress={player.playProgress}
          disabled={allEntries.length === 0 || (!player.isPlaying && !player.canPlay)}
          onClick={player.isPlaying ? player.stop : startPlaying}
        />
        {onUndo && (
          <ModalAction icon={<UndoIcon />} label={t('dialogs.chordBars.undo')} disabled={!canUndo} onClick={onUndo} />
        )}
        {onRedo && (
          <ModalAction icon={<RedoIcon />} label={t('dialogs.chordBars.redo')} disabled={!canRedo} onClick={onRedo} />
        )}
        {/* The same levels as the chart player's */}
        <ModalAction
          icon={<SlidersIcon />}
          label={t('player.volume')}
          className="volume-btn"
          active={isMixerOpen}
          onClick={() => setIsMixerOpen((open) => !open)}
        />
        {/* Dropped from under the toolbar, as in the chart player */}
        <ChordBarsMixer
          isOpen={isMixerOpen}
          onClose={() => setIsMixerOpen(false)}
          player={player}
          idPrefix="intensity"
          drums
        />
      </div>
      <div className="body">
        <div className="chart">
          {/* The levels a bar's height reads against, ruled across the shape behind it */}
          <div className="scale" aria-hidden="true">
            {[...INTENSITY_LEVELS].reverse().map((step) => (
              <span key={step.id}>{getIntensityLabel(step.id)}</span>
            ))}
          </div>
          <div
            className={cx('shape', { playing: Boolean(playingId), picking: selected.length > 0 })}
            role="group"
            aria-label={t('intensity.songShape')}
          >
            {arrangeable.map((part) => (
              <button
                key={part.id}
                type="button"
                className={cx('shape-part', {
                  selected: selectedIds.includes(part.id),
                  playing: part.id === playingId,
                })}
                onClick={() => onToggle(part.id)}
                aria-pressed={selectedIds.includes(part.id)}
                aria-label={part.chartDrop ? t('intensity.partDrops', { part: fullName(part) }) : fullName(part)}
              >
                <span className="shape-passes">
                  {Array.from({ length: repeatsOf(part) }, (unused, index) => (
                    <span
                      key={index}
                      className={cx('shape-bar', { sounding: part.id === playingId && segment.pass === index + 1 })}
                      style={{ height: `${getPassIntensity(passesOf(part), index + 1) * 20}%` }}
                    />
                  ))}
                </span>
                <span className={cx('shape-pad', { held: padHeldIds.has(part.id) })} />
                <span className="shape-label">
                  {part.chartDrop && <RestIcon className="shape-drop" />}
                  {/* Full names where the column is wide enough for them, else the short one */}
                  {<span className="name-full">{fullName(part)}</span>}
                  <span className="name-short">{shortName(part)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
        {/* Only what the shape is showing, so a mark is explained the moment it first turns up */}
        {(hasDrops || padHeldIds.size > 0) && (
          <div className="legend">
            {hasDrops && (
              <span className="legend-item">
                <RestIcon className="shape-drop" />
                {t('intensity.dropsBefore')}
              </span>
            )}
            {padHeldIds.size > 0 && (
              <span className="legend-item">
                <span className="legend-pad" />
                {t('intensity.padHeld')}
              </span>
            )}
          </div>
        )}
        {selected.length === 0 && (
          <div className="empty">
            <p>{t('intensity.empty')}</p>
            <button type="button" className="text-btn" onClick={() => onSelect(arrangeable.map((part) => part.id))}>
              {t('intensity.pickEvery')}
            </button>
          </div>
        )}
        {selected.length > 0 && (
          <section className="editor" aria-label={t('intensity.pickedParts')}>
            <div className="editor-header">
              <span className="editor-title">
                {selected.length === 1 ? fullName(selected[0]) : t('intensity.partCount', { count: selected.length })}
              </span>
              <button
                type="button"
                className="text-btn"
                onClick={() => onSelect(arrangeable.map((part) => part.id))}
                disabled={allPicked}
              >
                {t('intensity.all')}
              </button>
              <button type="button" className="text-btn" onClick={() => onSelect([])}>
                {t('intensity.clear')}
              </button>
            </div>
            <div className="settings">
              <div className="setting">
                <span className="setting-label">
                  <IntensityIcon />
                  {t('player.intensity')}
                </span>
                <div className="segmented" role="group" aria-label={t('player.intensity')}>
                  {INTENSITY_LEVELS.map((step) => (
                    <button
                      key={step.id}
                      type="button"
                      className={cx('segment', { active: level === step.id })}
                      onClick={() => onApply({ intensity: step.id })}
                      aria-pressed={level === step.id}
                    >
                      {getIntensityLabel(step.id)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="setting">
                <span className="setting-label">
                  <FillBarIcon />
                  {t('intensity.leadIn')}
                </span>
                <div className="chips">
                  <button
                    type="button"
                    className={cx('chip', { active: fill === true })}
                    onClick={() => onApply({ fill: fill !== true })}
                    aria-pressed={fill === null ? 'mixed' : fill}
                  >
                    {t('intensity.drumFill')}
                  </button>
                  <button
                    type="button"
                    className={cx('chip', { active: drop === true })}
                    onClick={() => onApply({ drop: drop !== true })}
                    aria-pressed={drop === null ? 'mixed' : drop}
                  >
                    {t('intensity.dropBeat')}
                  </button>
                </div>
              </div>
              <div className="setting">
                <span className="setting-label">
                  <PadIcon />
                  {t('player.pad')}
                </span>
                <div className="segmented" role="group" aria-label={t('player.pad')}>
                  {PAD_OPTIONS.map((id) => (
                    <button
                      key={id}
                      type="button"
                      className={cx('segment', { active: padSetting === id })}
                      onClick={() => onApply({ pad: id === PAD_AS_BEFORE ? null : id })}
                      aria-pressed={padSetting === id}
                    >
                      {t(`intensity.pad.${id}`)}
                    </button>
                  ))}
                </div>
                {padSetting && <p className="setting-note">{t(`intensity.pad.${padSetting}Note`)}</p>}
              </div>
              <div className="setting">
                <span className="setting-label">
                  <SoundIcon />
                  {t('intensity.sound')}
                </span>
                <InstrumentRow
                  instrumentId={partInstrument ?? instrumentOf(selected[0])}
                  mixed={partInstrument === null}
                  onSelect={(id) => onApply({ instrument: id === songInstrumentId ? null : id })}
                  defaultId={songInstrumentId}
                  recallLast={false}
                  player={partPlayer}
                  browser={instrumentBrowser(partInstrument)}
                />
                <RhythmStylePicker
                  rhythm={{
                    choice: partRhythm ?? rhythmOf(selected[0]),
                    onChoose: (choice) => onApply({ rhythm: choice === songRhythm ? null : choice }),
                    timeSignature,
                  }}
                  mixed={partRhythm === null}
                  defaultChoice={songRhythm}
                  player={partPlayer}
                />
                <p className="setting-note">{t('intensity.soundNote')}</p>
              </div>
              {players.length > 0 && (
                <div className="setting">
                  <span className="setting-label">
                    <NotesIcon />
                    {t('intensity.players')}
                  </span>
                  <div className="players">
                    {players.map((id) => {
                      const { labelKey, Icon } = PLAYERS[id];
                      const label = t(labelKey);
                      const set = shared((part) => part.chartLayers?.[id] || LAYER_AUTO);

                      return (
                        <div key={id} className="player">
                          <span className="player-label">
                            <Icon />
                            {label}
                          </span>
                          <div className="segmented" role="group" aria-label={label}>
                            {LAYER_OPTIONS.map((option) => (
                              <button
                                key={option}
                                type="button"
                                className={cx('segment', { active: set === option })}
                                onClick={() => onApply({ layers: { [id]: option === LAYER_AUTO ? null : option } })}
                                aria-pressed={set === option}
                              >
                                {t(`intensity.${option}`)}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="setting-note">{t('intensity.playersNote')}</p>
                </div>
              )}
              {single && (
                <div className="setting">
                  <span className="setting-label">
                    <RepeatIcon />
                    {t('player.repeats')}
                  </span>
                  <div className="passes">
                    {Array.from({ length: repeatsOf(single) }, (unused, index) => {
                      const set = single.chartIntensityPasses?.[index] ?? null;
                      const passLabel = t('intensity.pass', { number: index + 1 });

                      return (
                        <div key={index} className="pass">
                          <span className="pass-label">{passLabel}</span>
                          <div className="segmented" role="group" aria-label={passLabel}>
                            <button
                              type="button"
                              className={cx('segment', { active: set === null })}
                              onClick={() => setPass(index, null)}
                              aria-pressed={set === null}
                            >
                              {t('intensity.auto')}
                            </button>
                            {INTENSITY_LEVELS.map((step) => (
                              <button
                                key={step.id}
                                type="button"
                                className={cx('segment', 'number', { active: set === step.id })}
                                onClick={() => setPass(index, step.id)}
                                aria-pressed={set === step.id}
                                aria-label={getIntensityLabel(step.id)}
                              >
                                {step.id}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
        {/* For the whole song rather than the parts picked, so shown whether or not any are */}
        {(onInstrumentChange || onRhythmChange || player.setEffect || player.hasMelody) && (
          <section className="editor" aria-label={t('intensity.song')}>
            <div className="editor-header">
              <span className="editor-title">{t('intensity.song')}</span>
            </div>
            <div className="settings">
              {(onInstrumentChange || onRhythmChange || player.hasMelody) && (
                <div className="setting general">
                  <span className="setting-label">
                    <SettingsIcon />
                    {t('intensity.general')}
                  </span>
                  {onInstrumentChange && (
                    <InstrumentRow
                      instrumentId={songInstrumentId}
                      onSelect={onInstrumentChange}
                      defaultId={DEFAULT_INSTRUMENT_ID}
                      recallLast={false}
                      player={player}
                      browser={instrumentBrowser(songInstrumentId)}
                    />
                  )}
                  {onRhythmChange && (
                    <RhythmStylePicker
                      rhythm={{ choice: chartRhythm || BLOCK_CHORDS, onChoose: onRhythmChange, timeSignature }}
                      defaultChoice={BLOCK_CHORDS}
                      player={player}
                    />
                  )}
                  {ownSoundCount > 0 && (
                    <p className="setting-note">{t('intensity.ownSound', { count: ownSoundCount })}</p>
                  )}
                  {/* Remembered on this device, not saved on the song */}
                  {player.hasMelody && (
                    <div className="chips">
                      <button
                        type="button"
                        className={cx('chip', { active: player.melodyOn })}
                        onClick={player.toggleMelody}
                        aria-pressed={player.melodyOn}
                        // Changes the notes scheduled, so it waits for Stop as the mixer's switch does
                        disabled={player.isPlaying || player.isLoading}
                      >
                        {t('intensity.playMelodies')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {player.setEffect && (
                <div className="setting effects">
                  <div className="setting-header">
                    <span className="setting-label">
                      <ReverbIcon />
                      {t('intensity.effects')}
                    </span>
                    <button
                      type="button"
                      className="text-btn"
                      onClick={player.clearEffects}
                      disabled={!hasEffects(player.effects)}
                    >
                      {t('intensity.dry')}
                    </button>
                  </div>
                  <ChordBarsEffects player={player} idPrefix="intensity" drums />
                  <p className="setting-note">{t('intensity.effectsNote')}</p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
      <div className="footer">
        <button type="button" className="suggest-btn" onClick={onSuggest} disabled={arrangeable.length === 0}>
          <LightBulbIcon />
          {t('songsMenu.suggest')}
        </button>
        <button
          type="button"
          className="reset-btn"
          onClick={() => onReset(resetScope.map((part) => part.id))}
          disabled={!isSet}
        >
          {selected.length > 0 ? t('common.reset') : t('intensity.resetAll')}
        </button>
        <button type="button" className="done-btn" onClick={onDone}>
          {t('common.done')}
        </button>
      </div>
    </div>
  );

  // Over the whole screen, out of the form's layout, like a modal
  return createPortal(panel, document.body);
};

IntensityPanel.propTypes = {
  parts: PropTypes.arrayOf(PropTypes.object).isRequired,
  partNumbers: PropTypes.object.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  song: PropTypes.shape({
    bpm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    timeSignature: PropTypes.string,
    chartInstrumentId: PropTypes.string,
    chartRhythm: PropTypes.string,
    chartEffects: PropTypes.object,
    category: PropTypes.string,
    /** The key as the form now says, and as the song was loaded — to put each part's MIDI in its key */
    key: PropTypes.string,
    sourceKey: PropTypes.string,
  }).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSuggest: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  /** Given the song's next chartEffects, as the mixer changes them */
  onEffectsChange: PropTypes.func,
  /** Given the style picked for the whole song, BLOCK_CHORDS included */
  onRhythmChange: PropTypes.func,
  /** Given the instrument picked for the whole song, DEFAULT_INSTRUMENT_ID included */
  onInstrumentChange: PropTypes.func,
  onUndo: PropTypes.func,
  onRedo: PropTypes.func,
  canUndo: PropTypes.bool,
  canRedo: PropTypes.bool,
};

export default IntensityPanel;
